// Lo que sabe tu agente (4.1, D7).
//
// Es la pantalla donde el dueño ve todo lo que su agente sabe de su negocio
// porque alguien se lo dijo o lo saco de un archivo.
//
// **Lista plana por fecha, sin una tarjeta por memoria.** Agrupar se hace
// subiendo un escalon de espacio, no metiendo cada linea en su caja: con
// noventa memorias, noventa cajas son noventa bordes que no distinguen nada.
//
// **El cliente no elige tipos en ninguna parte.** Se filtra por de donde salio
// -tu, el agente, un archivo-, que es lo que de verdad recuerda; ofrecerle
// filtrar por «corrección» o «situación» seria enseñarle el motor.
//
// **El filtro va pegado a la lista que filtra, y con su recuento.** Estuvo
// encima de la puerta hasta el 2026-09-21, y como la puerta no se filtra -lo
// que espera un si todavia no es de nadie- en un workspace con siete
// pendientes y cero aprobadas las tres pestañas enseñaban exactamente lo
// mismo. No estaba roto: filtraba una lista vacia debajo de un bloque que no
// cambia nunca, que se ve igual que estar roto.
//
// El numero al lado hace las otras dos cosas que faltaban: convierte «Se lo
// dijiste tu» de orden en etiqueta -una frase sola en un boton se lee como un
// imperativo-, y explica por que al pulsar no pasa nada.
//
// **Y se pinta siempre.** El 2026-09-21 se escondio cuando un solo origen lo
// dejaba sin nada que separar, y esconderlo resulto peor: el filtro se
// desvanecio de la pantalla y lo primero que se pregunta quien lo ve es que
// paso con las pestañas. Un control que aparece y desaparece cuesta mas de
// entender que uno que no quita nada, y con el recuento al lado ya no hace
// falta esconderlo: `De un archivo (0)` dice por si solo que ahi no hay nada.
//
// **Cada lista lleva su titulo, y el titulo dice si eso ya cuenta.** Es la
// unica diferencia que importa entre las dos mitades -lo de arriba espera tu
// si y no se usa; lo de abajo se lee en cada respuesta- y no estaba escrita en
// ningun sitio: se deducia de que unas traen dos botones y otras dos enlaces.
//
// **Aqui no se traduce ni se compone ningun texto de memoria.** La linea, el
// nombre del tipo y la frase de procedencia vienen escritos del servidor, que
// es donde vive la regla que los produce. Escribirlos tambien aqui seria
// tenerlos dos veces y que un dia dejen de coincidir.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { BusinessContext } from './BusinessContext'
import { MemoryRow } from './MemoryRow'
import { PendingGate } from './PendingGate'
import { TeachTheAgent } from './TeachTheAgent'
import { cn } from '@/lib/utils'
import {
  WHO_SAID_IT,
  workspacesService,
  type Pendiente,
  type WhatTheAgentKnows as Memoria,
  type WhoSaidIt,
} from '@/services/workspacesService'

export function WhatTheAgentKnows() {
  const { t } = useTranslation()
  const [memorias, setMemorias] = useState<Memoria[]>([])
  const [pendientes, setPendientes] = useState<Pendiente[]>([])
  const [cuantas, setCuantas] = useState(0)
  const [porQuien, setPorQuien] = useState<Record<string, number>>({})
  const [quien, setQuien] = useState<WhoSaidIt | undefined>(undefined)
  const [cargando, setCargando] = useState(true)
  // Como clave y no traducido: `t` cambia de identidad en cada render, y
  // meterlo en las dependencias del efecto lo hace recargar sin parar.
  const [error, setError] = useState(false)
  // Lo que obliga a volver a pedir la lista cuando se enseña algo nuevo. Sin
  // esto habria que recargar la pagina para ver lo que acabas de contarle.
  const [recargar, setRecargar] = useState(0)

  useEffect(() => {
    let stillMounted = true
    setCargando(true)
    Promise.all([workspacesService.loQueSabe(quien), workspacesService.pendientes()])
      .then(([datos, esperando]) => {
        if (!stillMounted) return
        setMemorias(datos.memorias)
        setCuantas(datos.cuantas)
        setPorQuien(datos.porQuien)
        setPendientes(esperando)
        setError(false)
      })
      .catch(() => stillMounted && setError(true))
      .finally(() => stillMounted && setCargando(false))
    return () => {
      stillMounted = false
    }
  }, [quien, recargar])

  const deTodos = WHO_SAID_IT.reduce((suma, origen) => suma + (porQuien[origen] ?? 0), 0)

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">{t('knowledge.lo_que_sabe.titulo')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('knowledge.lo_que_sabe.explicacion')}
            </p>
          </div>
          <TeachTheAgent onGuardada={() => setRecargar((n) => n + 1)} />
        </div>

        {/* El contexto va antes que la lista porque es lo primero que el
            agente lee en cada turno: el orden de la pantalla es el orden en
            que el agente mira. */}
        <BusinessContext />

        {error && (
          <p className="text-sm text-destructive">{t('knowledge.lo_que_sabe.error')}</p>
        )}

        {/* La puerta arriba de la lista y no en una pantalla aparte (D8): un
            buzon separado hay que acordarse de abrirlo, y lo que nadie abre se
            queda esperando para siempre. */}
        <PendingGate
          pendientes={pendientes}
          onResuelta={() => setRecargar((n) => n + 1)}
        />

        {/* Pegado a la lista que filtra, y con su recuento. Sin caja: son
            cuatro botones en una fila. */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">
            {t('knowledge.lo_que_sabe.ya_lo_usa', { count: deTodos })}
          </h3>

          <div className="flex flex-wrap gap-2">
            <Button
              variant={quien === undefined ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setQuien(undefined)}
            >
              {t('knowledge.lo_que_sabe.filtro.todo', { count: deTodos })}
            </Button>
            {WHO_SAID_IT.map((opcion) => (
              <Button
                key={opcion}
                variant={quien === opcion ? 'secondary' : 'ghost'}
                size="xs"
                onClick={() => setQuien(opcion)}
              >
                {t(`knowledge.lo_que_sabe.filtro.${opcion.replace(/\s/g, '_')}`, {
                  count: porQuien[opcion] ?? 0,
                })}
              </Button>
            ))}
          </div>
        </div>

        {!error && !cargando && memorias.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('knowledge.lo_que_sabe.vacio')}
          </p>
        )}

        <ul className="space-y-4">
          {memorias.map((memoria) => (
            <MemoryRow
              key={memoria.id}
              memoria={memoria}
              onCambiada={() => setRecargar((n) => n + 1)}
            />
          ))}
        </ul>

        {cuantas > memorias.length && (
          <p className={cn('text-xs text-muted-foreground')}>
            {t('knowledge.lo_que_sabe.no_caben', { count: cuantas - memorias.length })}
          </p>
        )}
      </div>
    </div>
  )
}
